import assert from "assert"
import { GLTF } from "three/examples/jsm/Addons.js"
import { color, uniform } from "three/tsl"
import * as THREE from "three/webgpu"
import { FolderApi } from "tweakpane"

import highwayPath from "./assets/highway-path.json"
import policeCarPath from "./assets/police-car-path.json"

const OBJECT_NAME = "Police-car"
const LIGHT_INTENSITY_ON = 2
const LIGHT_INTENSITY_OFF = 0
/** Duration in seconds for light intensity to fade between on/off */
const LIGHT_FADE_DURATION = 0.3

const POLICE_LIGHT_BLUE = 0x0000ff
const POLICE_LIGHT_RED = 0xff0000
const POLICE_LIGHT_EMISSIVE_INTENSITY = 0.5

const dummyObject = new THREE.Object3D()

type PathPoint = { x: number; y: number; z: number }
type PathData = { points: PathPoint[]; closed?: boolean }

type PoliceCarProps = {
  resources: { [key: string]: GLTF }
  scene: THREE.Scene
  debugFolder?: FolderApi
}

export default class PoliceCar {
  scene: THREE.Scene
  debugFolder?: FolderApi

  group!: THREE.Group
  model!: THREE.Object3D
  redLight!: THREE.PointLight
  blueLight!: THREE.PointLight
  curve?: THREE.CatmullRomCurve3
  speed = 0.0006
  t = 0

  /** Materials collected from the model (for debug tweaks) */
  private materials: THREE.MeshStandardMaterial[] = []

  /** Debug: line mesh showing the path (only when debugFolder is set) */
  pathLineMesh?: THREE.Line<THREE.BufferGeometry, THREE.LineBasicNodeMaterial>

  /** Current intensity values (lerped each frame for smooth fade) */
  private redLightIntensity = 0
  private blueLightIntensity = 0

  private tempPoint = new THREE.Vector3()
  private tempTangent = new THREE.Vector3()
  private tempLookAt = new THREE.Vector3()

  constructor({ resources, scene, debugFolder }: PoliceCarProps) {
    this.model = resources.blender.scene?.getObjectByName(
      OBJECT_NAME,
    ) as THREE.Object3D
    assert(this.model, `${OBJECT_NAME} model not found`)

    this.scene = scene
    this.debugFolder = debugFolder

    this.setCurve()

    this.group = new THREE.Group()
    if (this.curve) {
      this.curve.getPoint(this.t, this.tempPoint)
      this.group.position.copy(this.tempPoint)
      this.curve.getTangent(this.t, this.tempTangent)
      this.tempLookAt.copy(this.tempPoint).sub(this.tempTangent)
      dummyObject.position.copy(this.tempPoint)
      dummyObject.lookAt(this.tempLookAt)
      dummyObject.rotateY(Math.PI)
      this.group.quaternion.copy(dummyObject.quaternion)
    } else {
      this.group.position.set(0, 1, 0)
    }

    this.model.updateMatrixWorld(true)
    const box = new THREE.Box3().setFromObject(this.model)
    const center = box.getCenter(new THREE.Vector3())
    const size = box.getSize(new THREE.Vector3())
    this.model.position.copy(center.negate())
    this.group.add(this.model)

    const roofY = size.y / 2 + 0.05
    const lightOffset = 0.25

    this.redLight = new THREE.PointLight(0xff0000, 1, 10)
    this.redLight.position.set(lightOffset, roofY, 0)
    this.group.add(this.redLight)

    this.blueLight = new THREE.PointLight(0x0000ff, 1, 10)
    this.blueLight.position.set(-lightOffset, roofY, 0)
    this.group.add(this.blueLight)

    this.model.traverse((child) => {
      if (child instanceof THREE.Mesh && child.material) {
        const raw = child.material as
          | THREE.MeshStandardMaterial
          | THREE.MeshStandardMaterial[]
        const mats = Array.isArray(raw)
          ? raw.map((m) => m.clone() as THREE.MeshStandardMaterial)
          : [raw.clone() as THREE.MeshStandardMaterial]
        child.material = mats.length === 1 ? mats[0] : mats
        if (child.name === "Cube002_Blue_0") {
          mats.forEach((m) => {
            m.color.setHex(POLICE_LIGHT_BLUE)
            if ("emissive" in m) m.emissive.setHex(POLICE_LIGHT_BLUE)
            if ("emissiveIntensity" in m)
              m.emissiveIntensity = POLICE_LIGHT_EMISSIVE_INTENSITY
          })
        } else if (child.name === "Cube002_MaterialRed_0") {
          mats.forEach((m) => {
            m.color.setHex(POLICE_LIGHT_RED)
            if ("emissive" in m) m.emissive.setHex(POLICE_LIGHT_RED)
            if ("emissiveIntensity" in m)
              m.emissiveIntensity = POLICE_LIGHT_EMISSIVE_INTENSITY
          })
        }
        mats.forEach((m) => this.materials.push(m))
      }
    })

    this.scene.add(this.group)
    this.setupDebug()
  }

  setCurve() {
    const path = (policeCarPath as PathData).points?.length
      ? (policeCarPath as PathData)
      : (highwayPath as PathData)
    if (!path.points?.length) {
      return
    }
    const vertices = []
    for (let i = 0; i < path.points.length; i++) {
      const x = path.points[i].x
      const y = path.points[i].y
      const z = path.points[i].z
      vertices[i] = new THREE.Vector3(x, y, z)
    }
    this.curve = new THREE.CatmullRomCurve3(vertices)
  }

  update(params?: { delta: number; elapsed: number }) {
    if (params == null) return

    if (this.curve) {
      this.t += this.speed
      if (this.t >= 1) this.t = 0
      const t = this.t
      this.curve.getPoint(t, this.tempPoint)
      this.group.position.copy(this.tempPoint)
      this.curve.getTangent(t, this.tempTangent)
      this.tempLookAt.copy(this.tempPoint).sub(this.tempTangent)
      dummyObject.position.copy(this.tempPoint)
      dummyObject.lookAt(this.tempLookAt)
      dummyObject.rotateY(Math.PI)
      this.group.quaternion.copy(dummyObject.quaternion)
    }

    const second = Math.floor(params.elapsed)
    const redOn = second % 2 === 0
    const targetRed = redOn ? LIGHT_INTENSITY_ON : LIGHT_INTENSITY_OFF
    const targetBlue = redOn ? LIGHT_INTENSITY_OFF : LIGHT_INTENSITY_ON
    const t = Math.min(1, params.delta / LIGHT_FADE_DURATION)
    this.redLightIntensity += (targetRed - this.redLightIntensity) * t
    this.blueLightIntensity += (targetBlue - this.blueLightIntensity) * t
    this.redLight.intensity = this.redLightIntensity
    this.blueLight.intensity = this.blueLightIntensity
  }

  setupDebug() {
    if (!this.debugFolder) {
      return
    }
    const folder = this.debugFolder.addFolder({
      title: "🚔️ Police Car",
      expanded: false,
    })

    if (this.curve) {
      const velocitySettings = { speed: this.speed }
      folder
        .addBinding(velocitySettings, "speed", {
          min: 0,
          max: 0.01,
          step: 0.0001,
          label: "velocity",
        })
        .on("change", (ev) => {
          this.speed = ev.value
        })
    }

    if (this.materials.length > 0) {
      const first = this.materials[0]
      const materialSettings = {
        envMapIntensity: "envMapIntensity" in first ? first.envMapIntensity : 1,
        color: `#${("color" in first ? first.color : new THREE.Color(0x888888)).getHexString()}`,
        emissiveIntensity:
          "emissiveIntensity" in first ? first.emissiveIntensity : 0,
        metalness: "metalness" in first ? first.metalness : 0.5,
        roughness: "roughness" in first ? first.roughness : 0.5,
        emissive: `#${("emissive" in first ? first.emissive : new THREE.Color(0)).getHexString()}`,
      }

      const materialFolder = folder.addFolder({
        title: "Material",
        expanded: true,
      })

      // Env map intensity: most realistic fix for dark materials (environment light)
      if ("envMapIntensity" in first) {
        materialFolder
          .addBinding(materialSettings, "envMapIntensity", {
            label: "Env map (realistic)",
            min: 0,
            max: 3,
            step: 0.01,
          })
          .on("change", (ev) => {
            this.materials.forEach((m) => {
              if ("envMapIntensity" in m) m.envMapIntensity = ev.value
            })
          })
      }

      // Base color: brightens the texture (e.g. light gray = less black)
      if ("color" in first) {
        materialFolder
          .addBinding(materialSettings, "color", {
            label: "Color (brighten)",
            view: "color",
          })
          .on("change", (ev) => {
            const c = new THREE.Color(ev.value)
            this.materials.forEach((m) => {
              if ("color" in m) m.color = c
            })
          })
      }

      if ("emissiveIntensity" in first) {
        materialFolder
          .addBinding(materialSettings, "emissiveIntensity", {
            min: 0,
            max: 2,
            step: 0.01,
          })
          .on("change", (ev) => {
            this.materials.forEach((m) => {
              if ("emissiveIntensity" in m) m.emissiveIntensity = ev.value
            })
          })
      }

      materialFolder
        .addBinding(materialSettings, "metalness", {
          min: 0,
          max: 1,
          step: 0.01,
        })
        .on("change", (ev) => {
          this.materials.forEach((m) => {
            if ("metalness" in m) m.metalness = ev.value
          })
        })

      materialFolder
        .addBinding(materialSettings, "roughness", {
          min: 0,
          max: 1,
          step: 0.01,
        })
        .on("change", (ev) => {
          this.materials.forEach((m) => {
            if ("roughness" in m) m.roughness = ev.value
          })
        })

      materialFolder
        .addBinding(materialSettings, "emissive", { view: "color" })
        .on("change", (ev) => {
          const c = new THREE.Color(ev.value)
          this.materials.forEach((m) => {
            if ("emissive" in m) m.emissive = c
          })
        })
    }

    const redFolder = folder.addFolder({ title: "Red light", expanded: false })
    const redSettings = {
      position: {
        x: this.redLight.position.x,
        y: this.redLight.position.y,
        z: this.redLight.position.z,
      },
      color: `#${this.redLight.color.getHexString()}`,
    }
    redFolder
      .addBinding(redSettings, "position", {
        x: { min: -2, max: 2, step: 0.01 },
        y: { min: -2, max: 2, step: 0.01 },
        z: { min: -2, max: 2, step: 0.01 },
      })
      .on("change", (ev) => {
        this.redLight.position.set(ev.value.x, ev.value.y, ev.value.z)
      })
    redFolder
      .addBinding(redSettings, "color", { view: "color" })
      .on("change", (ev) => {
        this.redLight.color.set(ev.value)
      })

    const blueFolder = folder.addFolder({
      title: "Blue light",
      expanded: false,
    })
    const blueSettings = {
      position: {
        x: this.blueLight.position.x,
        y: this.blueLight.position.y,
        z: this.blueLight.position.z,
      },
      color: `#${this.blueLight.color.getHexString()}`,
    }
    blueFolder
      .addBinding(blueSettings, "position", {
        x: { min: -2, max: 2, step: 0.01 },
        y: { min: -2, max: 2, step: 0.01 },
        z: { min: -2, max: 2, step: 0.01 },
      })
      .on("change", (ev) => {
        this.blueLight.position.set(ev.value.x, ev.value.y, ev.value.z)
      })
    blueFolder
      .addBinding(blueSettings, "color", { view: "color" })
      .on("change", (ev) => {
        this.blueLight.color.set(ev.value)
      })

    if (this.curve) {
      this.createPathLine()
      const debugSettings = { showPath: true }
      folder
        .addBinding(debugSettings, "showPath", { label: "Show path" })
        .on("change", (ev) => {
          if (!this.pathLineMesh) return
          this.pathLineMesh.visible = ev.value
        })
    }
  }

  private createPathLine() {
    if (!this.curve) return
    const divisions = 200
    const points = this.curve.getPoints(divisions)
    const geometry = new THREE.BufferGeometry().setFromPoints(points)
    const material = new THREE.LineBasicNodeMaterial({
      colorNode: uniform(color("#ff0080")),
    })
    this.pathLineMesh = new THREE.Line(geometry, material)
    this.pathLineMesh.frustumCulled = false
    this.scene.add(this.pathLineMesh)
  }
}
